<script setup lang="ts">
import { onMounted, useId } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import { SelectInput, SimpleLoading, IconImage, SearchCombobox, ThemeProvider } from '@/shared/ui';
import CloseIcon from '@/shared/assets/icons/CloseIcon.svg';
import CheckMarkIcon from '@/shared/assets/icons/CheckMarkIcon.svg';
import ArrowDown from '@/shared/assets/icons/ArrowDown.svg';
import AddButton from '@/shared/assets/icons/AddButton.svg';
import DeleteButton from '@/shared/assets/icons/DeleteButton.svg';
import { useCreateMember } from '../../lib/useCreateMember';

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'saved'): void;
}>();

const radioGroupName = `hasFunctionalActivity-${useId()}`;

const {
  selectedUser,
  searchBy,
  searchResultOptions,
  memberSearchEmptyMessage,
  deptPositionPairs,
  isLoadingPositions,
  functionalActivitySlots,
  isSaving,
  isFormValid,
  canAddPair,
  canAddFunctionalActivity,
  getDeptOptions,
  getPositionOptions,
  getFunctionalActivityOptions,
  searchUsers,
  selectUserById,
  setDeptForPair,
  setPositionForPair,
  removePair,
  addPair,
  hasFunctionalActivity,
  setFunctionalActivity,
  removeFunctionalActivity,
  addFunctionalActivity,
  toggleFunctionalActivity,
  save,
  reset,
  initialize,
} = useCreateMember();

onMounted(() => initialize());

const searchByOptions = [
  { id: 'name', name: 'Nome' },
  { id: 'registrationNumber', name: 'Matrícula' },
];

async function handleSave() {
  const result = await save();
  if (result.success) {
    emit('saved');
    emit('close');
  }
}
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-[1000px] max-h-[90vh] shadow-lg overflow-hidden"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    @closed="reset"
  >
    <ThemeProvider class="flex flex-col flex-1 min-h-0">
      <div class="flex flex-shrink-0 items-center justify-between border-b border-gray-02 px-6 py-4">
        <span class="text-sm font-semibold text-gray-07">Novo membro</span>
        <button
          class="flex size-6 items-center justify-center"
          @click="$emit('close')"
        >
          <IconImage
            :src="CloseIcon"
            alt="Fechar"
            class="size-6"
          />
        </button>
      </div>

      <div class="flex min-h-0 flex-1 flex-col gap-6 overflow-x-hidden overflow-y-auto px-6 py-6">
        <div class="flex-shrink-0">
          <span class="mb-2 block text-sm font-semibold text-gray-07">Membro:</span>

          <div class="flex items-center gap-2">
            <div class="relative w-[140px] flex-shrink-0">
              <select
                v-model="searchBy"
                class="h-9 w-full cursor-pointer appearance-none rounded-md border border-gray-02 bg-white-01 pl-3 pr-8 text-sm text-gray-07 outline-none"
              >
                <option
                  v-for="opt in searchByOptions"
                  :key="opt.id"
                  :value="opt.id"
                >
                  {{ opt.name }}
                </option>
              </select>
              <div class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
                <IconImage
                  :src="ArrowDown"
                  alt=""
                  class="size-4"
                />
              </div>
            </div>

            <SearchCombobox
              class="flex-1"
              :model-value="selectedUser ? String(selectedUser.id) : ''"
              :options="searchResultOptions"
              trigger-icon="search"
              placeholder="Buscar membro..."
              search-placeholder="Buscar..."
              :empty-message="memberSearchEmptyMessage"
              @update:model-value="selectUserById"
              @search="searchUsers"
            />
          </div>
        </div>

        <hr class="flex-shrink-0 border-t border-gray-02">

        <div class="flex flex-shrink-0 flex-col space-y-4">
          <div
            v-for="(pair, index) in deptPositionPairs"
            :key="index"
            class="flex gap-4 items-end"
          >
            <div class="w-64 flex flex-col gap-2">
              <span
                v-if="index === 0"
                class="text-sm text-gray-07 font-semibold"
              >
                Departamento:
              </span>
              <SelectInput
                :model-value="pair.deptId ?? ''"
                :options="getDeptOptions(index)"
                label="Selecione um Departamento"
                container-class="w-full"
                :use-teleport="true"
                @update:model-value="setDeptForPair(index, $event ? Number($event) : null)"
              />
            </div>

            <div class="flex-1 flex flex-col gap-2">
              <span
                v-if="index === 0"
                class="text-sm text-gray-07 font-semibold"
              >
                Cargo:
              </span>
              <div class="relative">
                <SimpleLoading v-if="pair.deptId !== null && isLoadingPositions[pair.deptId]" />
                <SelectInput
                  v-else
                  :model-value="pair.positionId ?? ''"
                  :options="getPositionOptions(index)"
                  :disabled="pair.deptId === null || getPositionOptions(index).length === 0"
                  label="Selecione um Cargo"
                  container-class="w-full"
                  :use-teleport="true"
                  @update:model-value="setPositionForPair(index, $event ? Number($event) : null)"
                />
              </div>
            </div>

            <div class="flex items-center h-[42px]">
              <IconImage
                v-if="index === 0"
                :src="AddButton"
                alt="Adicionar"
                data-testid="add-pair-btn"
                :class="[
                  'text-[var(--theme-secondary-ink)]',
                  canAddPair ? 'cursor-pointer hover:opacity-80' : 'opacity-30 cursor-not-allowed',
                ]"
                @click="canAddPair && addPair()"
              />
              <IconImage
                v-else
                :src="DeleteButton"
                alt="Remover"
                data-testid="remove-pair-btn"
                class="cursor-pointer text-gray-05 hover:text-gray-07"
                @click="removePair(index)"
              />
            </div>
          </div>
        </div>

        <hr class="flex-shrink-0 border-t border-gray-02">

        <div class="flex-shrink-0">
          <p class="text-sm text-gray-07 font-semibold mb-2">
            Atividade Funcional:
          </p>

          <div class="flex items-center gap-6 mb-3">
            <label class="flex items-center gap-2 text-sm text-gray-07 cursor-pointer">
              <input
                type="radio"
                :name="radioGroupName"
                :checked="!hasFunctionalActivity"
                data-testid="fa-radio-no"
                class="accent-[var(--theme-secondary)]"
                @change="toggleFunctionalActivity(false)"
              >
              Não
            </label>
            <label class="flex items-center gap-2 text-sm text-gray-07 cursor-pointer">
              <input
                type="radio"
                :name="radioGroupName"
                :checked="hasFunctionalActivity"
                data-testid="fa-radio-yes"
                class="accent-[var(--theme-secondary)]"
                @change="toggleFunctionalActivity(true)"
              >
              Sim
            </label>
          </div>

          <div
            v-if="hasFunctionalActivity"
            class="flex flex-col space-y-4"
          >
            <div
              v-for="(slot, index) in functionalActivitySlots"
              :key="index"
              class="flex gap-4 items-end"
            >
              <div class="flex-1 flex flex-col gap-2">
                <SelectInput
                  :model-value="slot.id ?? ''"
                  :options="getFunctionalActivityOptions(index)"
                  label="Selecione uma Atividade"
                  container-class="w-full"
                  :use-teleport="true"
                  @update:model-value="setFunctionalActivity(index, $event ? Number($event) : null)"
                />
              </div>

              <div class="flex items-center h-[42px]">
                <IconImage
                  v-if="index === 0"
                  :src="AddButton"
                  alt="Adicionar"
                  data-testid="add-fa-btn"
                  :class="[
                    'text-[var(--theme-secondary-ink)]',
                    canAddFunctionalActivity ? 'cursor-pointer hover:opacity-80' : 'opacity-30 cursor-not-allowed',
                  ]"
                  @click="canAddFunctionalActivity && addFunctionalActivity()"
                />
                <IconImage
                  v-else
                  :src="DeleteButton"
                  alt="Remover"
                  :data-testid="`remove-fa-btn-${index}`"
                  class="cursor-pointer text-gray-05 hover:text-gray-07"
                  @click="removeFunctionalActivity(index)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-shrink-0 justify-end gap-6 border-t border-gray-02 px-6 py-8">
        <button
          class="h-[44px] px-4 bg-white-01 border border-gray-02 rounded-lg text-sm font-medium text-gray-07 hover:bg-gray-50 disabled:opacity-50"
          :disabled="isSaving"
          @click="$emit('close')"
        >
          Cancelar
        </button>
        <button
          class="h-[42px] px-4 rounded-lg text-sm font-medium flex items-center gap-2 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] disabled:cursor-not-allowed transition-colors"
          :class="isFormValid && !isSaving ? 'bg-[var(--theme-secondary)] text-white' : 'bg-light-gray-xx-02 text-white/40'"
          :disabled="!isFormValid || isSaving"
          @click="handleSave"
        >
          <IconImage
            :src="CheckMarkIcon"
            alt=""
            class="size-5"
            :class="isFormValid && !isSaving ? 'text-white' : 'opacity-40'"
          />
          Salvar
        </button>
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>
